import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Download, Upload } from 'lucide-react';
import DataTable from '../common/DataTable';
import UnitForm from './UnitForm';
import ConfirmationModal from '../common/ConfirmationModal';
import { inventoryService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmation } from '../../hooks/useConfirmation';

interface Unit {
  id: number;
  name: string;
  symbol: string;
  parent_id?: number;
  conversion_factor: number;
  is_active: boolean;
}

const UnitManagement: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | undefined>(undefined);
  const [resetForm, setResetForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { showToast } = useToast();
  const { confirmationState, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();

  useEffect(() => {
    loadUnits(1);
  }, []);

  const loadUnits = async (page: number, search?: string) => {
    try {
      setLoading(true);
      const searchText = search !== undefined ? search : searchTerm;
      const response = await inventoryService.getUnits({ 
        page, 
        per_page: 10, 
        search: searchText 
      });
      setUnits(response.data);
      setTotalItems(response.total || 0);
      setCurrentPage(response.page || page);
      if (search !== undefined) {
        setSearchTerm(search);
      }
    } catch (error) {
      showToast('error', 'Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (search: string) => {
    setSearchTerm(search);
    if (search) {
      await loadUnits(1, search);
    } else {
      await loadUnits(1, '');
    }
  };

  const handleEdit = (unit: Unit) => {
    setEditingUnit(unit);
  };

  const handleSave = async (unitData: any) => {
    try {
      if (editingUnit) {
        await inventoryService.updateUnit(editingUnit.id, unitData);
        showToast('success', 'Unit updated successfully');
      } else {
        await inventoryService.createUnit(unitData);
        showToast('success', 'Unit created successfully');
      }
      setEditingUnit(undefined);
      setResetForm(true);
      setTimeout(() => setResetForm(false), 100);
      loadUnits(currentPage);
    } catch (error) {
      showToast('error', 'Failed to save unit');
    }
  };

  const handleCancel = () => {
    setEditingUnit(undefined);
    setResetForm(true);
    setTimeout(() => setResetForm(false), 100);
  };

  const handleImport = async () => {
    try {
      showToast('success', 'Units imported successfully');
      loadUnits(1);
    } catch (error) {
      showToast('error', 'Failed to import units');
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await inventoryService.downloadUnitsTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'units_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('success', 'Template downloaded successfully');
    } catch (error) {
      showToast('error', 'Failed to download template');
    }
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      await inventoryService.importUnits(file);
      showToast('success', 'Units imported successfully');
      loadUnits(1);
    } catch (error) {
      showToast('error', 'Failed to import units');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleToggleCollapse = () => {
    setIsFormCollapsed(!isFormCollapsed);
  };

  const handleDelete = async (unit: Unit) => {
    showConfirmation(
      {
        title: 'Delete Unit',
        message: `Are you sure you want to delete unit "${unit.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        variant: 'danger'
      },
      async () => {
        try {
          await inventoryService.deleteUnit(unit.id);
          showToast('success', 'Unit deleted successfully');
          loadUnits(currentPage);
        } catch (error) {
          showToast('error', 'Failed to delete unit');
        }
      }
    );
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Unit Name' },
    { key: 'symbol', label: 'Symbol' },
    {
      key: 'parent_id',
      label: 'Parent Unit',
      render: (value: number, row: Unit) => {
        if (!value) return '-';
        const parentUnit = units.find(u => u.id === value);
        return parentUnit ? parentUnit.name : `ID: ${value}`;
      }
    },
    { key: 'conversion_factor', label: 'Conversion Factor' },
    {
      key: 'is_active',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <div style={{ padding: 'var(--erp-spacing-lg)' }}>
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 flex justify-between items-center" style={{ padding: 'var(--erp-section-padding)' }}>
          <h2 className="font-medium text-gray-800" style={{ fontSize: 'var(--erp-datatable-title-font-size)', lineHeight: 'var(--erp-line-height)' }}>
            {editingUnit ? 'Edit Unit' : 'Add New Unit'}
          </h2>
          <div className="flex items-center" style={{ gap: 'var(--erp-spacing-sm)' }}>
            <button
              type="button"
              onClick={downloadTemplate}
              className="erp-form-btn erp-btn-template"
            >
              <Download className="erp-form-btn-icon" />
              Template
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="erp-form-btn erp-btn-import"
            >
              <Upload className="erp-form-btn-icon" />
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileImport}
              className="hidden"
            />
            <button
              type="button"
              onClick={handleToggleCollapse}
              className="text-gray-500 hover:text-gray-700"
            >
              {isFormCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>

        {!isFormCollapsed && (
          <UnitForm
            unit={editingUnit}
            onSave={handleSave}
            onCancel={handleCancel}
            isCollapsed={false}
            onToggleCollapse={() => {}}
            resetForm={resetForm}
          />
        )}
      </div>
      
      <DataTable
        title="Unit Management"
        columns={columns}
        data={units}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        pageSize={10}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={(page) => loadUnits(page)}
        onSearch={handleSearch}
      />
      
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        variant={confirmationState.variant}
        onConfirm={handleConfirm}
        onCancel={hideConfirmation}
      />
    </div>
  );
};

export default UnitManagement;